import type { MatomoConfig } from '@shared/config';
import { catalog, type ReaderEvent } from '@shared/events';

import type { Destination } from '../destination';
import { lazySdk, urlOf } from '../lazy-sdk';
import { titleOf } from '../mapping';

/**
 * Matomo's action-scoped custom dimensions, by id. **The ids are the same
 * on every tenant site of the Canvasflow instance**, which must have these
 * dimensions created in this order (and at least this many action slots).
 * Changing an id here changes what every site records under it.
 *
 * One dimension per field a person would filter by in Matomo. The two JSON
 * dimensions carry what is only ever read back from raw rows by `system`:
 * `row` holds the fields that are unique or nearly unique per event (a
 * dimension each would cost a useless report apiece), and `params` holds the
 * event's own parameters, so a new event never needs a new slot.
 */
export const MATOMO_DIMENSIONS = {
	issue_id: 1,
	article_id: 2,
	page_number: 3,
	content_type: 4,
	content_language: 5,
	offline: 6,
	display_mode: 7,
	language: 8,
	identity_level: 9,
	schema_version: 10,
	app_id: 11,
	row: 12,
	params: 13,
	build_version: 14,
} as const;

/** Matomo cuts a dimension's value at 255 characters. */
export const MATOMO_MAX_VALUE_LENGTH = 255;

const ROW_FIELDS = ['event_id', 'event_ts', 'session_id', 'reader_id'] as const;
const PLAIN_FIELDS = [
	'issue_id',
	'article_id',
	'page_number',
	'content_type',
	'content_language',
	'offline',
	'display_mode',
	'language',
	'identity_level',
	'schema_version',
	'app_id',
	'build_version',
] as const;
// An event's main number rides in Matomo's own event value as well.
const VALUE_FIELDS = ['dwell_ms', 'listened_ms', 'watched_ms'];

type Fields = Record<string, string | number | boolean | undefined>;

/**
 * JSON that always fits one dimension and always parses: if it is too long,
 * the longest text in it is shortened until it fits, never the JSON itself.
 */
function packJson(fields: Fields): string | undefined {
	const packed: Fields = {};
	for (const [key, value] of Object.entries(fields)) {
		if (value !== undefined) {
			packed[key] = value;
		}
	}
	if (!Object.keys(packed).length) {
		return undefined;
	}
	let json = JSON.stringify(packed);
	while (json.length > MATOMO_MAX_VALUE_LENGTH) {
		const longest = Object.entries(packed)
			.filter((entry): entry is [string, string] => typeof entry[1] === 'string')
			.sort((a, b) => b[1].length - a[1].length)[0];
		if (!longest || !longest[1].length) {
			return undefined;
		}
		const excess = json.length - MATOMO_MAX_VALUE_LENGTH;
		packed[longest[0]] = longest[1].slice(
			0,
			Math.max(0, longest[1].length - excess),
		);
		json = JSON.stringify(packed);
	}
	return json;
}

function dimensionsOf(event: ReaderEvent): Record<string, string> {
	const fields = event as unknown as Fields;
	const dimensions: Record<string, string> = {};
	const set = (key: keyof typeof MATOMO_DIMENSIONS, value: unknown) => {
		if (value !== undefined) {
			dimensions[`dimension${MATOMO_DIMENSIONS[key]}`] = String(value).slice(
				0,
				MATOMO_MAX_VALUE_LENGTH,
			);
		}
	};

	for (const key of PLAIN_FIELDS) {
		set(key, fields[key]);
	}
	set('row', packJson(Object.fromEntries(ROW_FIELDS.map((k) => [k, fields[k]]))));

	const own = Object.keys(catalog[event.name].params.shape).filter(
		// A page view's path is already the URL Matomo records.
		(key) => !(event.name === 'screen_view' && key === 'path'),
	);
	set('params', packJson(Object.fromEntries(own.map((k) => [k, fields[k]]))));
	return dimensions;
}

/**
 * Matomo, on the Canvasflow instance with one site per tenant app.
 * `screen_view` is a page view. Every other event is a Matomo event:
 * category = the event's family, action = its name, name = the title of the
 * content it is about (for a person reading Matomo's Events report; reports
 * use the id dimensions), value = its main number when it has one.
 *
 * Dimensions are passed with each request, never through
 * `setCustomDimension`, which would leave one event's article on the next.
 * No user id is set.
 */
export function createMatomoDestination(config: MatomoConfig): Destination {
	const matomo = lazySdk(() =>
		import('@canvasflow/analytics').then(
			({ MatomoTracker }) =>
				new MatomoTracker({
					urlBase: config.urlBase,
					siteId: config.siteId,
					trackerUrl: config.trackerUrl || undefined,
					srcUrl: config.srcUrl || undefined,
					disabled: config.disabled,
					heartBeat: config.heartBeat,
					linkTracking: config.linkTracking ?? true,
					configurations: config.configurations ?? {},
				}),
		),
	);
	let optedOut = false;

	return {
		name: 'matomo',
		start: () => {
			matomo.load();
			if (optedOut) {
				optedOut = false;
				matomo.use((tracker) => tracker.pushInstruction('forgetUserOptOut'));
			}
		},
		stop: () => {
			optedOut = true;
			matomo.use((tracker) => tracker.pushInstruction('optUserOut'));
		},
		send: (event) => {
			const dimensions = dimensionsOf(event);
			const title = titleOf(event);
			matomo.use((tracker) => {
				if (event.name === 'screen_view') {
					tracker.pushInstruction('setCustomUrl', urlOf(event.path));
					tracker.pushInstruction(
						'trackPageView',
						title ?? document.title,
						dimensions,
					);
					return;
				}
				const fields = event as unknown as Fields;
				const value = VALUE_FIELDS.map((key) => fields[key]).find(
					(candidate) => typeof candidate === 'number',
				);
				tracker.pushInstruction(
					'trackEvent',
					catalog[event.name].family,
					event.name,
					title,
					value,
					dimensions,
				);
			});
		},
	};
}
